/*
 * Copyright (c) 2020. Dave van Rijn Development
 */

import {BaseModel, createFieldDef} from "./baseModel";
import {FieldDefinition, UserStatus, UserType} from "../util/interfaces";
import {Moment} from "moment";
import {IndexedObject, ResponseData, ModelResponse, toMoment} from '@dvrd/dvr-controls';
import {getUser} from "../util/requestSender";

interface Props {
    id?: string;
    created_at?: string;
    updated_at?: string;
    last_login?: string;
    email?: string;
    firstname?: string;
    infix?: string;
    lastname?: string;
    status?: UserStatus;
    flex?: object;
    type?: UserType;
    two_factor_enabled?: number | boolean;
}

interface Fields {
    id: string;
    created_at: string;
    updated_at: string;
    last_login: string;
    email: string;
    firstname: string;
    infix: string;
    lastname: string;
    status: UserStatus;
    flex: IndexedObject<any>;
    type: UserType;
    two_factor_enabled: boolean;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    last_login: '',
    email: '',
    firstname: '',
    infix: '',
    lastname: '',
    status: UserStatus.ACTIVE,
    flex: {},
    type: UserType.USER,
    two_factor_enabled: false,
};

const fieldsDefinition: IndexedObject<FieldDefinition> = {
    id: createFieldDef('string'),
    created_at: createFieldDef('string'),
    updated_at: createFieldDef('string', undefined, true),
    last_login: createFieldDef('string', undefined, true),
    email: createFieldDef('string'),
    firstname: createFieldDef('string'),
    infix: createFieldDef('string'),
    lastname: createFieldDef('string'),
    status: createFieldDef('string', UserStatus.ACTIVE),
    flex: createFieldDef('object',),
    type: createFieldDef('string'),
    two_factor_enabled: createFieldDef('number', false, false, false, true),
};

const skipFields: string[] = [];

export default class User extends BaseModel<Props, Fields> {
    static get = (callback: ModelResponse<User | null>) => {
        getUser({
            callback: (data: ResponseData) => {
                const user = data.success ? new User(data.user) : null;
                callback(user, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(skipFields, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id;
    }

    set id(id: string) {
        this.fields.id = id;
    }

    get createdAt(): string {
        return this.fields.created_at;
    }

    set createdAt(created_at: string) {
        this.fields.created_at = created_at;
    }

    get createdAtMoment(): Moment {
        return toMoment(this.createdAt);
    }

    get updatedAt(): string {
        return this.fields.updated_at;
    }

    set updatedAt(updated_at: string) {
        this.fields.updated_at = updated_at;
    }

    get updatedAtMoment(): Moment {
        return toMoment(this.updatedAt);
    }

    get lastLogin(): string {
        return this.fields.last_login;
    }

    set lastLogin(last_login: string) {
        this.fields.last_login = last_login;
    }

    get lastLoginMoment(): Moment {
        return toMoment(this.lastLogin);
    }

    get email(): string {
        return this.fields.email;
    }

    set email(email: string) {
        this.fields.email = email;
    }

    get firstname(): string {
        return this.fields.firstname;
    }

    set firstname(firstname: string) {
        this.fields.firstname = firstname;
    }

    get infix(): string {
        return this.fields.infix;
    }

    set infix(infix: string) {
        this.fields.infix = infix;
    }

    get lastname(): string {
        return this.fields.lastname;
    }

    set lastname(lastname: string) {
        this.fields.lastname = lastname;
    }

    get fullname(): string {
        let name = this.firstname;
        if (this.infix.length) name += ` ${this.infix}`;
        if (this.lastname.length) name += ` ${this.lastname}`;
        return name;
    }

    get status(): UserStatus {
        return this.fields.status;
    }

    set status(status: UserStatus) {
        this.fields.status = status;
    }

    get flex(): IndexedObject<any> {
        return this.fields.flex;
    }

    set flex(flex: IndexedObject<any>) {
        this.fields.flex = flex;
    }

    get type(): UserType {
        return this.fields.type;
    }

    set type(type: UserType) {
        this.fields.type = type;
    }

    get twoFactorEnabled(): boolean {
        return this.fields.two_factor_enabled;
    }

    set twoFactorEnabled(enabled: boolean) {
        this.fields.two_factor_enabled = enabled;
    }

    protected build = (props?: Props) => {
        if (props) {
            if (typeof props.two_factor_enabled === 'number')
                this.twoFactorEnabled = props.two_factor_enabled === 1;
        }
    };
}